import { useMemo, useRef, useState, type MouseEvent } from 'react'
import { max as d3Max } from 'd3-array'
import type { MonthlyByServiceRow } from '@/components/gcp/gcpHelpers'
import { formatAxisUsd, formatMonthLabel, formatUsd, niceAxisScale } from '@/components/gcp/gcpHelpers'

const CHART_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#059669',
  '#d97706',
  '#dc2626',
  '#6b7280',
]

interface MonthlyTrendChartProps {
  rows: MonthlyByServiceRow[]
}

interface MonthStack {
  month: string
  total: number
  segments: Array<{ service: string; value: number; color: string }>
}

interface HoverState {
  x: number
  y: number
  month: string
  service: string
  value: number
  total: number
}

export default function MonthlyTrendChart({ rows }: MonthlyTrendChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [activeService, setActiveService] = useState<string | null>(null)

  const { stacks, services } = useMemo(() => {
    if (rows.length === 0) return { stacks: [] as MonthStack[], services: [] as string[] }

    const serviceTotals = new Map<string, number>()
    for (const row of rows) {
      serviceTotals.set(row.service, (serviceTotals.get(row.service) ?? 0) + row.net_cost)
    }

    const topServices = [...serviceTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([service]) => service)

    const months = [...new Set(rows.map((r) => r.month))].sort()
    const stacks: MonthStack[] = months.map((month) => {
      const monthRows = rows.filter((r) => r.month === month)
      const otherValue = monthRows
        .filter((r) => !topServices.includes(r.service))
        .reduce((sum, r) => sum + r.net_cost, 0)

      const segments = topServices.map((service, index) => ({
        service,
        value: monthRows.find((r) => r.service === service)?.net_cost ?? 0,
        color: CHART_COLORS[index % CHART_COLORS.length]!,
      }))

      if (otherValue > 0) {
        segments.push({ service: 'Other', value: otherValue, color: CHART_COLORS[5]! })
      }

      return {
        month,
        total: monthRows.reduce((sum, r) => sum + r.net_cost, 0),
        segments,
      }
    })

    const legendServices = [...topServices]
    if (stacks.some((stack) => stack.segments.some((seg) => seg.service === 'Other'))) {
      legendServices.push('Other')
    }

    return { stacks, services: legendServices }
  }, [rows])

  if (stacks.length === 0) {
    return <p className="text-sm text-gray-500">No monthly billing data available.</p>
  }

  const width = 720
  const height = 280
  const margin = { top: 16, right: 16, bottom: 40, left: 56 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom
  const dataMax = d3Max(stacks, (stack) => stack.total) ?? 0
  const { max: yMax, ticks: yTicks } = niceAxisScale(dataMax, 5)
  const gap = 10
  const barWidth = Math.max(24, innerWidth / stacks.length - gap)

  const updateHover = (
    event: MouseEvent<SVGRectElement>,
    stack: MonthStack,
    segment: MonthStack['segments'][number]
  ) => {
    const chart = chartRef.current
    if (!chart) return
    const rect = chart.getBoundingClientRect()
    setHover({
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top + 12,
      month: stack.month,
      service: segment.service,
      value: segment.value,
      total: stack.total,
    })
  }

  return (
    <div ref={chartRef} className="relative space-y-3">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-full"
        role="img"
        aria-label="Monthly spend trend by service"
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {yTicks.map((value) => {
            const y = innerHeight - (yMax > 0 ? (value / yMax) * innerHeight : 0)
            return (
              <g key={value}>
                <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#e5e7eb" />
                <text x={-8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[11px]">
                  {formatAxisUsd(value)}
                </text>
              </g>
            )
          })}

          {stacks.map((stack, index) => {
            const x = index * (barWidth + gap) + gap / 2
            let yOffset = innerHeight
            return (
              <g key={stack.month} transform={`translate(${x},0)`}>
                {stack.segments.map((segment) => {
                  const barHeight = yMax > 0 ? (segment.value / yMax) * innerHeight : 0
                  yOffset -= barHeight
                  const dimmed =
                    activeService != null && activeService !== segment.service
                  return (
                    <rect
                      key={`${stack.month}-${segment.service}`}
                      x={0}
                      y={yOffset}
                      width={barWidth}
                      height={Math.max(barHeight, 0)}
                      fill={segment.color}
                      opacity={dimmed ? 0.2 : 1}
                      className="cursor-pointer transition-opacity"
                      onMouseEnter={(e) => {
                        setActiveService(segment.service)
                        updateHover(e, stack, segment)
                      }}
                      onMouseMove={(e) => updateHover(e, stack, segment)}
                      onMouseLeave={() => {
                        setActiveService(null)
                        setHover(null)
                      }}
                    />
                  )
                })}
                <text
                  x={barWidth / 2}
                  y={innerHeight + 18}
                  textAnchor="middle"
                  className="fill-gray-600 text-[11px]"
                >
                  {formatMonthLabel(stack.month)}
                </text>
              </g>
            )
          })}
          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
        </g>
      </svg>

      {hover ? (
        <div
          className="pointer-events-none absolute z-10 min-w-[10rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{ left: hover.x, top: hover.y }}
        >
          <p className="font-medium text-gray-900">{formatMonthLabel(hover.month)}</p>
          <p className="mt-1 text-gray-700">
            {hover.service}: {formatUsd(hover.value)}
          </p>
          <p className="text-gray-500">Month total: {formatUsd(hover.total)}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        {services.map((service, index) => (
          <button
            key={service}
            type="button"
            className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-opacity ${
              activeService != null && activeService !== service ? 'opacity-40' : ''
            }`}
            onMouseEnter={() => setActiveService(service)}
            onMouseLeave={() => setActiveService(null)}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
            />
            {service}
          </button>
        ))}
      </div>
    </div>
  )
}
